import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiAlertTriangle, FiCheckCircle, FiArrowRight } from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useUserData } from '../utils/UserDataContext';

const FraudDetection: React.FC = () => {
  const { userData, updateFraudAlertStatus } = useUserData();
  const { fraudAlerts } = userData.aiInsights;

  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);

  // Mark an alert as resolved
  const handleMarkAsResolved = (id: string) => {
    updateFraudAlertStatus(id, 'resolved');
  };

  // Mark an alert as reviewing
  const handleInvestigate = (id: string) => {
    updateFraudAlertStatus(id, 'reviewing');
    setSelectedAlert(id);
  };

  // Calculate statistics
  const stats = {
    total: fraudAlerts.length,
    high: fraudAlerts.filter(alert => alert.severity === 'high').length,
    medium: fraudAlerts.filter(alert => alert.severity === 'medium').length,
    low: fraudAlerts.filter(alert => alert.severity === 'low').length,
    resolved: fraudAlerts.filter(alert => alert.status === 'resolved').length,
    reviewing: fraudAlerts.filter(alert => alert.status === 'reviewing').length,
    new: fraudAlerts.filter(alert => alert.status === 'new').length
  };

  // Security tips
  const securityTips = [
    "Enable two-factor authentication on all financial accounts",
    "Use unique passwords for each financial website",
    "Regularly monitor your accounts for suspicious activity",
    "Never share personal or financial information via email or phone",
    "Be cautious of phishing attempts and suspicious links"
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background animate-fadeIn">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">Fraud Protection</h1>
            <p className="text-text/70">Monitor your accounts for suspicious activities and get real-time fraud alerts</p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card variant="glass" className="p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                  <FiShield className="text-primary" size={24} />
                </div>
                <div>
                  <p className="text-text/70 text-sm">Protection Status</p>
                  <p className="text-lg font-semibold">Active</p>
                </div>
              </div>
            </Card>
            
            <Card variant="glass" className="p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mr-3">
                  <FiAlertTriangle className="text-red-500" size={24} />
                </div>
                <div>
                  <p className="text-text/70 text-sm">Total Alerts</p>
                  <p className="text-lg font-semibold">{stats.total}</p>
                </div>
              </div>
            </Card>
            
            <Card variant="glass" className="p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center mr-3">
                  <FiAlertTriangle className="text-yellow-500" size={24} />
                </div>
                <div>
                  <p className="text-text/70 text-sm">New Alerts</p>
                  <p className="text-lg font-semibold">{stats.new}</p>
                </div>
              </div>
            </Card>
            
            <Card variant="glass" className="p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mr-3">
                  <FiCheckCircle className="text-green-500" size={24} />
                </div>
                <div>
                  <p className="text-text/70 text-sm">Resolved</p>
                  <p className="text-lg font-semibold">{stats.resolved}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Fraud Alerts */}
          <Card variant="glass" className="p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4">Fraud Alerts</h2>
            
            {fraudAlerts.length > 0 ? (
              <div className="space-y-4">
                {fraudAlerts.map(alert => {
                  const isResolved = alert.status === 'resolved';
                  const isReviewing = alert.status === 'reviewing';
                  const isSelected = selectedAlert === alert.id;
                  
                  return (
                    <div 
                      key={alert.id} 
                      className={`p-4 border rounded-lg transition-all duration-200 ${
                        isSelected ? 'ring-2 ring-primary ring-opacity-50' : ''
                      } ${
                        isResolved 
                          ? 'bg-green-500/5 border-green-500/20' 
                          : isReviewing
                            ? 'bg-blue-500/5 border-blue-500/20'
                            : alert.severity === 'high'
                              ? 'bg-red-500/5 border-red-500/20'
                              : alert.severity === 'medium'
                                ? 'bg-yellow-500/5 border-yellow-500/20'
                                : 'bg-blue-500/5 border-blue-500/20'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center mb-1">
                            {isResolved ? (
                              <FiCheckCircle className="text-green-500 mr-2" size={16} />
                            ) : isReviewing ? (
                              <FiAlertTriangle className="text-blue-500 mr-2" size={16} />
                            ) : alert.severity === 'high' ? (
                              <FiAlertTriangle className="text-red-500 mr-2" size={16} />
                            ) : alert.severity === 'medium' ? (
                              <FiAlertTriangle className="text-yellow-500 mr-2" size={16} />
                            ) : (
                              <FiAlertTriangle className="text-blue-500 mr-2" size={16} />
                            )}
                            <span className={`text-sm px-2 py-0.5 rounded-full ${
                              isResolved 
                                ? 'bg-green-500/10 text-green-500' 
                                : isReviewing
                                  ? 'bg-blue-500/10 text-blue-500'
                                  : alert.severity === 'high'
                                    ? 'bg-red-500/10 text-red-500'
                                    : alert.severity === 'medium'
                                      ? 'bg-yellow-500/10 text-yellow-500'
                                      : 'bg-blue-500/10 text-blue-500'
                            }`}>
                              {isResolved 
                                ? 'Resolved' 
                                : isReviewing
                                  ? 'Under Review'
                                  : alert.severity === 'high'
                                    ? 'High Severity'
                                    : alert.severity === 'medium'
                                      ? 'Medium Severity'
                                      : 'Low Severity'
                              }
                            </span>
                            <span className="text-text/50 text-xs ml-2">{alert.date}</span>
                          </div>
                          <p className="font-medium">{alert.description}</p>
                        </div>
                        
                        <div className="flex space-x-2">
                          {!isResolved && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleMarkAsResolved(alert.id)}
                            >
                              Mark as Resolved
                            </Button>
                          )}
                          
                          {alert.status === 'new' ? (
                            <Button 
                              size="sm"
                              onClick={() => handleInvestigate(alert.id)}
                            >
                              Investigate
                            </Button>
                          ) : isReviewing ? (
                            <Button 
                              size="sm"
                              variant={isSelected ? "primary" : "outline"}
                              onClick={() => setSelectedAlert(isSelected ? null : alert.id)}
                            >
                              {isSelected ? "Hide Details" : "View Details"}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      
                      {/* Investigation Details */}
                      {isSelected && isReviewing && (
                        <div className="mt-4 p-4 bg-background/50 rounded-lg border border-border animate-fadeIn">
                          <h4 className="font-medium mb-2">Investigation Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-text/70">Status:</span>
                              <span className="font-medium">Under Review</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-text/70">Initiated:</span>
                              <span className="font-medium">Just now</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-text/70">Estimated resolution:</span>
                              <span className="font-medium">24-48 hours</span>
                            </div>
                          </div>
                          <p className="mt-3 text-text/70 text-sm">
                            Our security team is investigating this alert. We'll notify you once the investigation is complete.
                          </p>
                          <div className="mt-3 flex justify-end">
                            <Button size="sm" variant="outline" onClick={() => handleMarkAsResolved(alert.id)}>
                              Dismiss Alert
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-text/50">
                <FiCheckCircle size={48} className="mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No fraud alerts detected</p>
                <p className="mb-4">Your accounts are currently secure. We'll notify you if any suspicious activity occurs.</p>
                <Link to="/account-security" className="inline-block">
                  <Button variant="outline" rightIcon={<FiArrowRight size={16} />}>
                    View Account Security
                  </Button>
                </Link>
              </div>
            )}
          </Card>

          {/* Security Tips */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card variant="glass" className="p-6 lg:col-span-2">
              <h2 className="text-lg font-semibold mb-4">Security Tips</h2>
              <ul className="space-y-3">
                {securityTips.map((tip, index) => (
                  <li key={index} className="flex">
                    <div className="mr-3 bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium">{index + 1}</span>
                    </div>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="mt-6" rightIcon={<FiArrowRight size={16} />}>
                More Security Tips
              </Button>
            </Card>

            <Card variant="glass" className="p-6">
              <h2 className="text-lg font-semibold mb-4">Security Settings</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">SMS Alerts</span>
                    <span className="text-sm text-green-500">Enabled</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Email Notifications</span>
                    <span className="text-sm text-green-500">Enabled</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Location Monitoring</span>
                    <span className="text-sm text-red-500">Disabled</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Two-Factor Authentication</span>
                    <span className="text-sm text-green-500">Enabled</span>
                  </div>
                </div>
                <Link to="/account-security">
                  <Button fullWidth className="mt-2">
                    Manage Security Settings
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FraudDetection; 